import React from "react";

/**
 * PartnersMarquee — continuously scrolling logo strip of premium hospitality
 * brands we collaborate with. Uses elegant text wordmarks as placeholders;
 * swap each entry's `text` with an `<img>` once official logos are received.
 *
 * To replace with real logos later, change a brand entry to:
 *   { text: "Taj", logo: "/partners/taj.svg" }
 * The `LogoSlot` will render `logo` as a normalised image when present.
 */

const HOSPITALITY_BRANDS = [
  { text: "Taj", style: "serif" },
  { text: "Oberoi", style: "serif" },
  { text: "Marriott", style: "mono" },
  { text: "Hyatt", style: "mono" },
  { text: "ITC Hotels", style: "editorial" },
  { text: "The Leela", style: "serif" },
  { text: "Radisson", style: "mono" },
  { text: "Hilton", style: "mono" },
  { text: "Accor", style: "editorial" },
  { text: "Westin", style: "mono" },
  { text: "Fairmont", style: "serif" },
  { text: "Roseate", style: "editorial" },
];

const Wordmark = ({ children, style = "serif" }) => (
  <span
    className="inline-flex items-center"
    style={{
      fontFamily:
        style === "serif"
          ? "'Cormorant Garamond', serif"
          : style === "editorial"
          ? "'Playfair Display', serif"
          : "'Outfit', sans-serif",
      fontWeight: style === "mono" ? 600 : 500,
      letterSpacing: style === "mono" ? "0.22em" : "0.05em",
      fontSize: "24px",
      color: "#3B312D",
      textTransform: style === "mono" ? "uppercase" : "none",
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

export default function PartnersMarquee() {
  const loop = [...HOSPITALITY_BRANDS, ...HOSPITALITY_BRANDS]; // duplicate for seamless loop
  return (
    <section
      data-testid="partners-section"
      className="relative py-20 md:py-28 bg-[#FAF6F1] overflow-hidden border-t border-b border-[rgba(182,89,128,0.12)]"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
        <p className="pill mx-auto" data-testid="partners-overline">
          Trusted Network
        </p>
        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-5 text-[#3B312D] leading-[1.05]">
          Partnered with the <span className="italic text-[#B65980]">best in hospitality</span> and celebrations.
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-[16px] md:text-[17px] text-[#3B312D]/75 leading-relaxed">
          From iconic luxury hotels and palace resorts to trusted decor, catering and production partners — we collaborate with the finest names to bring every celebration to life.
        </p>
      </div>

      {/* Marquee */}
      <div
        className="relative mt-12 md:mt-16 group"
        style={{
          maskImage:
            "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)",
        }}
      >
        <div
          className="marquee-track items-center"
          style={{ animationDuration: "55s" }}
          data-testid="partners-marquee-track"
        >
          {loop.map((brand, i) => (
            <LogoSlot key={`${brand.text}-${i}`} brand={brand} />
          ))}
        </div>
      </div>

      <p className="mt-10 md:mt-12 text-center text-[11px] tracking-[0.24em] uppercase text-[#8A9A7B] font-semibold">
        Hotels · Palaces · Resorts · Banquets · Convention Centres
      </p>
    </section>
  );
}

// Uniform container normalising every logo (rectangular, square, circular)
// into the same visual weight so the row reads as one elegant line.
const LogoSlot = ({ brand }) => (
  <div
    className="shrink-0 mx-5 md:mx-8 flex items-center justify-center"
    data-testid={`partner-logo-${brand.text.toLowerCase().replace(/\s+/g, "-")}`}
    style={{
      height: 96,
      minWidth: 200,
      padding: "0 26px",
      borderRadius: 18,
      background: "rgba(255,255,255,0.55)",
      border: "1px solid rgba(59,49,45,0.06)",
      transition: "transform .5s ease, box-shadow .5s ease, border-color .5s ease, background .5s ease",
      backdropFilter: "blur(4px)",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = "translateY(-3px)";
      e.currentTarget.style.borderColor = "rgba(182,89,128,0.28)";
      e.currentTarget.style.boxShadow = "0 16px 30px -22px rgba(59,49,45,0.25)";
      e.currentTarget.style.background = "rgba(255,255,255,0.85)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.borderColor = "rgba(59,49,45,0.06)";
      e.currentTarget.style.boxShadow = "none";
      e.currentTarget.style.background = "rgba(255,255,255,0.55)";
    }}
  >
    <div
      className="flex items-center justify-center"
      style={{ maxHeight: 60, maxWidth: 220 }}
    >
      {brand.logo ? (
        <img
          src={brand.logo}
          alt={brand.text}
          style={{ maxHeight: 60, maxWidth: 200, objectFit: "contain" }}
        />
      ) : (
        <Wordmark style={brand.style}>{brand.text}</Wordmark>
      )}
    </div>
  </div>
);
